import { RefObject, useEffect } from 'react';

/**
 * Las cabeceras de la grilla son cortas («APGAR 1'», «Sem.») para que entren, y `RColumn` solo acepta texto en `header`.
 * Esto les agrega el nombre completo como tooltip de Novarium (`nv-tip` + `data-pr-tooltip`, que se ve con el mouse y con
 * el foco de las cabeceras ordenables) y como descripción accesible (`aria-description`). `tips` va de la cabecera corta,
 * tal cual está escrita, a su nombre completo. La grilla redibuja sus cabeceras al cargar y al redimensionar, así que se
 * observa el DOM (altas y bajas de nodos y su clase; `apply` solo escribe lo que falta, así no se dispara a sí mismo).
 */
export default function useColumnHeaderTips(containerRef: RefObject<HTMLElement>, tips: Record<string, string>): void {
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return undefined;

        const apply = () => {
            container.querySelectorAll<HTMLElement>('th').forEach((th) => {
                const title = th.querySelector('.p-column-title')?.textContent?.trim();
                const full = title ? tips[title] : undefined;
                if (!full) return;
                // Solo se escribe lo que falta: así lo que se observa no se dispara a sí mismo
                if (!th.classList.contains('nv-tip')) th.classList.add('nv-tip');
                if (th.getAttribute('data-pr-tooltip') !== full) th.setAttribute('data-pr-tooltip', full);
                if (th.getAttribute('aria-description') !== full) th.setAttribute('aria-description', full);
            });
        };

        apply();
        const observer = new MutationObserver(apply);
        // También la clase: la grilla la reescribe al ordenar y se perdería `nv-tip`
        observer.observe(container, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
        return () => observer.disconnect();
    }, [containerRef, tips]);
}
